import React from "react";
import PropTypes from "prop-types";
import Ripple from "./ripple";
import styled from "styled-components";
import { theme } from "../theme";
import { rgba } from "polished";
import { Flex } from "./layout";

// Define the ButtonStyle with size variants and shape handling
const FabButtonStyle = styled(({ component: Element = "button", ...props }) => (
  <Element {...props} />
))((props) => {
  const {
    color,
    Colorvariant = "main",
    variant = "icon",
    shape = "rounded",
    sx = {},
    disabled,
    size = "medium",
  } = props;
  const colorObj = theme.colors[color] || theme.colors.main;
  const bgColorValue = colorObj[Colorvariant] || colorObj.main;
  const colorLight = rgba(bgColorValue, 0.2);
  let backgroundColor,
    textColor,
    padding,
    border,
    fontSize,
    iconSize,
    butttonSize,
    borderRadius;

  // Size variations
  switch (size) {
    case "xs":
      padding =
        shape === "extended"
          ? variant === "outlined"
            ? "0.125rem 0.5rem"
            : "0.25rem 0.5rem"
          : 0;
      fontSize = "0.75rem";
      iconSize = "1rem";
      butttonSize = "1.5rem";
      break;
    case "sm":
      padding =
        shape === "extended"
          ? variant === "outlined"
            ? "0.2875rem 0.75rem"
            : "0.375rem 0.75rem"
          : 0;
      fontSize = "0.875rem";
      iconSize = "1.3125rem";
      butttonSize = "2.0625rem";
      break;
    case "lg":
      padding =
        shape === "extended"
          ? variant === "outlined"
            ? "0.6875rem 1.5rem"
            : "0.75rem 1.5rem"
          : 0;
      fontSize = "1.25rem";
      iconSize = "1.875rem";
      butttonSize = "3.25rem";
      break;
    default: // medium
      padding =
        shape === "extended"
          ? variant === "outlined"
            ? "0.4375rem 1.25rem"
            : "0.5rem 1.25rem"
          : "0";
      fontSize = "1rem";
      iconSize = "1.5rem";
      butttonSize = "2.5rem";
  }

  // Variant handling
  switch (variant) {
    case "outlined":
      backgroundColor = "transparent";
      border = `${rgba(bgColorValue, 1)} solid 0.0625rem`;
      break;
    case "filled":
      backgroundColor = colorLight;
      border = "transparent";
      break;
    case "contained":
      backgroundColor = bgColorValue;
      border = "transparent";
      break;
    default: // icon
      backgroundColor = "transparent";
      border = "transparent";
  }

  // Shape handling
  switch (shape) {
    case "box":
      borderRadius = theme.radius.element;
      break;
    case "extended":
      borderRadius = "2.5rem";
      break;
    case "rounded": // Updated for rounded
      borderRadius = "50%";
      break;
    default:
      borderRadius = "50%";
  }

  // Color handling for text and hover effects
  switch (color) {
    case "primary":
      textColor = ["outlined", "filled", "icon"].includes(variant)
        ? theme.colors.primary.main
        : theme.colors.light.light;
      break;
    case "secondary":
      textColor = ["outlined", "filled", "icon"].includes(variant)
        ? theme.colors.secondary.main
        : theme.colors.light.light;
      break;
    case "success":
      textColor = ["outlined", "filled", "icon"].includes(variant)
        ? theme.colors.success.main
        : theme.colors.light.light;
      break;
    case "danger":
      textColor = ["outlined", "filled", "icon"].includes(variant)
        ? theme.colors.danger.main
        : theme.colors.light.light;
      break;
    case "dark":
      textColor = ["outlined", "filled", "icon"].includes(variant)
        ? theme.colors.dark.main
        : theme.colors.light.light;
      break;  
    case "light":
      textColor = ["outlined", "filled", "icon"].includes(variant)
        ? theme.colors.light.main
        : theme.colors.primary.light;
      break;  
    default:
      textColor = theme.colors.default.dark;
  }

  return {
    width: shape === "extended" ? "auto" : butttonSize,
    height: butttonSize,
    backgroundColor,
    color: textColor,
    textAlign: "center",
    cursor: disabled ? "not-allowed" : "pointer",
    padding,
    borderRadius,
    fontSize,
    fontWeight: 500,
    lineHeight: 1.5,
    outline: "none",
    border,
    overflow: "hidden",
    position: "relative",
    textTransform: "capitalize",
    opacity: disabled ? 0.6 : 1,
    fontFamily: theme.typography.fontFamily,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& .iconBtn": {
      width: iconSize,
      height: iconSize,
      alignItems: "center",
      justifyContent: "center",
      marginInlineEnd: shape === "extended" && "0.5rem",
    },
    "&:hover": {
      backgroundColor: disabled
        ? backgroundColor
        : variant === "outlined"
          ? rgba(bgColorValue, 0.15)
          : variant === "filled"
            ? rgba(bgColorValue, 0.35)
            : variant === "contained"
              ? rgba(bgColorValue, 0.9)
              : rgba(bgColorValue, 0.1), // icon hover
    },
    ...sx,
  };
});

// FabButton component definition
export const FabButton = ({
  children,
  icon,
  color = "default",
  shape = "rounded",
  className,
  disabled,
  onClick,
  Colorvariant,
  variant = "icon",
  sx,
  size = "medium",
}) => {
  const colorObj = theme.colors[color] || theme.colors.dark;
  let rippleColor = colorObj.light;

  // Determine ripple color based on variant
  if (["outlined", "icon", "filled"].includes(variant)) {
    rippleColor = colorObj.light;
  } else {
    rippleColor = theme.colors.light.light;
  }

  return (
    <FabButtonStyle
      color={color}
      Colorvariant={Colorvariant}
      disabled={disabled}
      onClick={onClick}
      className={className}
      variant={variant}
      shape={shape}
      sx={sx}
      size={size}
    >
      <Flex className="iconBtn">{icon}</Flex>
      
        {children}

    <Ripple color={rippleColor} duration={1000} />
    </FabButtonStyle>
  );
};

FabButton.defaultProps = {
  children: null,              // Default is no children
  icon: null,                  // Default is no icon
  color: "default",            // Default color is 'default'
  Colorvariant: "main",        // Default color variant is 'main'
  shape: "rounded",            // Default shape is 'rounded'
  className: "",               // Default is no additional class name
  disabled: false,             // Button is enabled by default
  onClick: () => {},           // Default onClick is a no-op function
  variant: "icon",             // Default button variant is 'icon'
  size: "medium",              // Default size is 'medium'
  sx: {},                      // Default is no custom styles
};

FabButton.propTypes = {
  /** Content inside the button (text or icon) */
  children: PropTypes.node,

  /** Icon inside the button */
  icon: PropTypes.node,

  /** Color of the button (from the theme) */
  color: PropTypes.oneOf(Object.keys(theme.colors)),

  /** Variant of the color (main, light, dark) */
  Colorvariant: PropTypes.oneOf(["main", "light", "dark"]),

  /** Button shape: rounded, box, or extended */
  shape: PropTypes.oneOf(["rounded", "box", "extended"]),

  /** Additional className for styling */
  className: PropTypes.string,

  /** If true, the button is disabled */
  disabled: PropTypes.bool,

  /** Click event handler */
  onClick: PropTypes.func,

  /** Variant of the button (outlined, icon, filled, contained) */
  variant: PropTypes.oneOf(["icon", "outlined", "filled", "contained"]),

  /** Size of the button (small, medium, large) */
  size: PropTypes.oneOf(["sm", "md", "lg"]),

  /** Custom sx styles */
  sx: PropTypes.object,
};
